import React, { useState, useEffect, useRef } from 'react';
import { useStore } from '@nanostores/react';
import { isSearchOpen, searchTerm } from '@stores/searchStore';
import { track } from '@vercel/analytics';

import SearchFlyoutNosto from './SearchFlyout/SearchFlyoutNosto.jsx';
import { getSearchResults } from '../../api/nosto.js';
import SearchIcon from './SearchIcon/SearchIcon.jsx';
import MALogomark from '@components/Icons/MALogomark.jsx';
import SearchStatic from './SearchStatic/SearchStatic.jsx';
import staticData from './SearchData/staticData.js';
import { filterNostoProducts } from '@utils/nostoValidationUtils.js';
import { getNostoSessionParamsWithFallback } from '@utils/nostoSessionUtils.js';
import { recordSearchSubmit, recordSearchAutocomplete } from '@utils/nostoTracking.js';

const SearchNostoUI = () => {
    const $isSearchOpen = useStore(isSearchOpen || false);
    const $searchTerm = useStore(searchTerm || '');

    const [query, setQuery] = useState($searchTerm.q || '');
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [hitCount, setHitCount] = useState(0);
    const [suggestions, setSuggestions] = useState([]);
    const [searched, setSearched] = useState(false);
    const [collections, setCollections] = useState([]);
    const [pages, setPages] = useState([]);
    const [redirect, setRedirect] = useState(null);
    const [isScrolled, setIsScrolled] = useState(false);
    const debounceRef = useRef();
    const inputElement = useRef(null);
    const scrollContainerRef = useRef(null);

    const handleSearch = async (searchTerm = query) => {
        if (!searchTerm.trim()) {
            setResults([]);
            setHitCount(0);
            setSearched(false);
            setCollections([]);
            setPages([]);
            setRedirect(null);
            return;
        }
        setLoading(true);

        // Get session params for personalization with timeout
        let sessionParams = null;
        try {
            const sessionPromise = getNostoSessionParamsWithFallback();
            const timeoutPromise = new Promise((resolve) => setTimeout(() => resolve(null), 3000));
            sessionParams = await Promise.race([sessionPromise, timeoutPromise]);
        } catch (error) {
            sessionParams = null;
        }

        const searchResults = await getSearchResults(searchTerm, 500, sessionParams);

        const hits = searchResults?.data?.search?.products?.hits || [];
        const filtered = filterNostoProducts(hits, {
            includeOutOfStock: true,
            enableLogging: true,
            context: `Search Component: "${searchTerm}"`
        });
        setResults(filtered);
        setHitCount(filtered.length);
        setLoading(false);
        setSearched(true);
        setRedirect(searchResults?.data?.search?.redirect || null);

        // Record search submit analytics
        if (filtered.length > 0) {
            console.log('🔍 SearchNosto Debug - Calling recordSearchSubmit:', {
                searchTerm,
                filteredLength: filtered.length,
                filtered: filtered
            });

            const response = recordSearchSubmit(searchTerm, filtered);
            console.log('🔍 SearchNosto Debug - recordSearchSubmit response:', response);
        }
    };

    const handleInput = (val) => {
        clearTimeout(debounceRef.current);
        if (!val.trim()) {
            setSuggestions([]);
            setSearched(false);
            setCollections([]);
            setPages([]);
            setRedirect(null);
            return;
        }
        debounceRef.current = setTimeout(async () => {
            try {
                // Get session params for personalization with timeout
                let sessionParams = null;
                try {
                    const sessionPromise = getNostoSessionParamsWithFallback();
                    const timeoutPromise = new Promise((resolve) => setTimeout(() => resolve(null), 3000));
                    sessionParams = await Promise.race([sessionPromise, timeoutPromise]);
                } catch (error) {
                    sessionParams = null;
                }

                // PERFORMANCE OPTIMIZATION: Limit search flyout results to 8 for faster loading
                let results;
                try {
                    results = await getSearchResults(val, 8, sessionParams);
                } catch (error) {
                    results = await getSearchResults(val, 8, null);
                }

                const keywords = results?.data?.search?.keywords?.hits || [];

                if (Array.isArray(keywords) && keywords.length > 0) {
                    const keywordList = keywords.map(k => k.keyword);
                    setSuggestions(keywordList);
                } else {
                    setSuggestions([]);
                }

                // Handle search results separately with enhanced filtering
                if (results?.data?.search?.products?.hits?.length > 0) {
                    const hits = results.data.search.products.hits;

                    const filtered = filterNostoProducts(hits, {
                        includeOutOfStock: true,
                        enableLogging: true,
                        context: `Search Suggestions: "${val}"`
                    });

                    setResults(filtered);
                    setHitCount(filtered.length);
                    setSearched(true);

                    // Record autocomplete analytics
                    if (filtered.length > 0) {
                        console.log('🔍 SearchNosto - Calling recordSearchAutocomplete with:', {
                            query: val,
                            filteredCount: filtered.length,
                            sampleFiltered: filtered[0]
                        });
                        recordSearchAutocomplete(val, filtered);
                    }
                } else {
                    setResults([]);
                    setHitCount(0);
                    setSearched(true);
                }

                // Handle collections and pages
                setCollections(results?.data?.search?.collections?.hits || []);
                setPages(results?.data?.search?.pages?.hits || []);

                // Handle redirect
                setRedirect(results?.data?.search?.redirect || null);
            } catch (error) {
                console.error('🔍 SearchNosto - Error fetching search results:', error);
                setSuggestions([]);
                setCollections([]);
                setPages([]);
                setRedirect(null);
            }
        }, 300);
    };

    const handleSuggestionClick = (keyword) => {
        setQuery(keyword);
        handleSearch(keyword);
    };

    const handleClearSearch = () => {
        setQuery('');
        setResults([]);
        setHitCount(0);
        setSearched(false);
        setSuggestions([]);
        setRedirect(null);
    };

    const handleCancelSearch = () => {
        if (isSearchOpen) {
            isSearchOpen.set(false);
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            isSearchOpen.set(false);

            // First perform the search to get filtered results
            if (query) {
                handleSearch(query).then(() => {
                    // Record search submit analytics with the filtered results
                    console.log('🔍 SearchNosto Debug - Calling recordSearchSubmit:', {
                        searchTerm: query,
                        filteredLength: results.length,
                        filtered: results
                    });

                    const response = recordSearchSubmit(query, results);
                    console.log('🔍 SearchNosto Debug - recordSearchSubmit response:', response);
                });
            }

            setTimeout(() => {
                window.location.href = `/search/${encodeURIComponent(query)}`;
            }, 100);
        }
        if (e.key === 'Escape') {
            handleCancelSearch();
        }
    };

    // Handle input changes including paste events
    const handleInputChange = (e) => {
        const value = e.target.value;
        setQuery(value);
        handleInput(value);
    };

    // Handle paste events specifically
    const handlePaste = (e) => {
        // Let the onChange handle the paste, but ensure it triggers
        setTimeout(() => {
            const value = e.target.value;
            if (value && value !== query) {
                handleInput(value);
            }
        }, 0);
    };

    // Inject global styles for search-scrolled state
    useEffect(() => {
        const styleId = 'search-scrolled-styles';
        if (!document.getElementById(styleId)) {
            const style = document.createElement('style');
            style.id = styleId;
            style.textContent = `
                body.search-scrolled .search-popup > div {
                    margin-left: 1rem;
                    margin-right: 1rem;
                    border-radius: 0.5rem;
                    box-shadow: 0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1);
                }
            `;
            document.head.appendChild(style);
        }
        return () => {
            const style = document.getElementById(styleId);
            if (style) {
                style.remove();
            }
        };
    }, []);

    useEffect(() => {
        if (inputElement.current) {
            inputElement.current.focus();
        }
    }, []);

    // Focus input when search modal opens
    useEffect(() => {
        if ($isSearchOpen && inputElement.current) {
            // Small delay to ensure the modal is fully rendered
            setTimeout(() => {
                inputElement.current.focus();
            }, 50);
        }
    }, [$isSearchOpen]);

    useEffect(() => {
        const keyDownHandler = (e) => {
            if (e.key === 'Escape') {
                e.preventDefault();
                handleCancelSearch();
            }
        };

        document.addEventListener('keydown', keyDownHandler);
        return () => {
            document.removeEventListener('keydown', keyDownHandler);
        };
    }, []);

    // Watch for query changes and update suggestions
    useEffect(() => {
        if (query && query.trim()) {
            // Debounce the suggestion update
            const timer = setTimeout(() => {
                handleInput(query);
            }, 300);
            return () => clearTimeout(timer);
        } else {
            setSuggestions([]);
        }
    }, [query]);

    useEffect(() => {
        const timer = setTimeout(() => {
            if (query) {
                track('Search', { query: query });
            }
        }, 2000);
        return () => clearTimeout(timer);
    }, [query]);

    // Add/remove body class when search opens/closes
    useEffect(() => {
        if ($isSearchOpen) {
            document.body.classList.add('search-open');
        } else {
            document.body.classList.remove('search-open');
        }
    }, [$isSearchOpen]);

    // Track scroll position and update styling - Similar to NavScrollHandler
    useEffect(() => {
        if (!$isSearchOpen) return;

        // Only run on desktop (same check as NavScrollHandler)
        if (window.innerWidth < 1024) {
            return;
        }

        const handleScroll = () => {
            const scrollY = window.pageYOffset;
            const scrolled = scrollY > 0;

            setIsScrolled(scrolled);

            // Add/remove class to body for global styling
            if (scrolled) {
                document.body.classList.add('search-scrolled');
            } else {
                document.body.classList.remove('search-scrolled');
            }
        };

        window.addEventListener('scroll', handleScroll, { passive: true });

        // Check initial scroll position
        handleScroll();

        return () => {
            window.removeEventListener('scroll', handleScroll);
            document.body.classList.remove('search-scrolled');
        };
    }, [$isSearchOpen]);

    // Search term tracking - similar to Search.jsx
    useEffect(() => {
        // Setup before functions
        let typingTimer; // timer identifier
        let doneTypingInterval = 2000; // time in ms (2 seconds)
        let myInput = document.getElementById('nosto-search-input');

        if (!myInput) return;

        // On keyup, start the countdown
        const handleKeyUp = () => {
            clearTimeout(typingTimer);
            if (myInput.value) {
                typingTimer = setTimeout(doneTyping, doneTypingInterval);
            }
        };

        // User is "finished typing," do something
        function doneTyping() {

            dataLayer = window.dataLayer || [];
            dataLayer.push({
                event: 'GA4_search_term',
                search_term: query,
            });
        }

        myInput.addEventListener('keyup', handleKeyUp);

        // Cleanup
        return () => {
            if (myInput) {
                myInput.removeEventListener('keyup', handleKeyUp);
            }
            clearTimeout(typingTimer);
        };
    }, [$isSearchOpen, query]);

    return (
        <div
            className={`transition-all duration-200  ${
                $isSearchOpen
                    ? `fixed left-1/2 -translate-x-1/2 bg-white z-[200] lg:rounded-b-lg ${
                        isScrolled
                            ? 'top-0 lg:top-[16px] w-full lg:w-[calc(100%-2rem)] lg:rounded-lg lg:shadow-lg overflow-hidden'
                            : 'top-0 lg:top-[32px] w-full shadow-md lg:rounded-b-lg'
                    }`
                    : 'hidden'
            }`}
        >
            <div
                ref={scrollContainerRef}
                className="overflow-y-auto max-h-[calc(100vh-32px)] md:max-h-[calc(100vh-64px)] lg:max-h-[586px] overscroll-contain"
                style={{ WebkitOverflowScrolling: 'touch', touchAction: 'pan-y' }}
            >
                <div className={`w-full flex justify-center sticky top-0 bg-white z-10 ${isScrolled ? 'border-b border-stone-300' : 'px-4 lg:px-0'}`}>
                    <div className="search-input-outer w-full flex items-center wrap-xl mx-auto xl:px-10 px-4">
                        <div className="search-icon-scroll hidden lg:block">
                            <MALogomark width="w-[32px]" />
                        </div>
                    <div className='search-input-inner flex items-center justify-center py-3 h-full relative font-akkurat wrap-md w-full mx-auto'>

                        <span className='search-icon w-5 absolute left-4'>
                            <SearchIcon />
                        </span>
                        <input
                            className={`w-full h-[37px] pl-12 mr-3 lg:mr-0 bg-background-secondary-subtle rounded-full appearance-none font-normal ${
                                query ? 'pr-16 text-text-primary' : 'pr-3'
                            }`}
                            id='nosto-search-input'
                            type='search'
                            placeholder='Search'
                            value={query}
                            onChange={handleInputChange}
                            onInput={handleInputChange}
                            onPaste={handlePaste}
                            autoFocus
                            ref={inputElement}
                            onKeyDown={handleKeyPress}
                            inputMode='search'
                            autoCapitalize='none'
                        />
                        {query && (
                            <button
                                type="button"
                                onClick={handleClearSearch}
                                className="absolute right-[40px] lg:right-3 top-1/2 -translate-y-1/2 text-text-secondary hover:text-text-primary font-normal text-sm underline cursor-pointer z-10 bg-transparent"
                            >
                                Clear
                            </button>
                        )}

                    </div>
                    <div className='cancel-search'>
                            <span
                                className='cancel-icon flex items-center text-text-primary hover:text-text-primary font-akkurat cursor-pointer text-xs lg:text-base'
                                onClick={handleCancelSearch}
                            >
                                <span className='-top-[2px] relative lg:mr-1 text-text-primary text-2xl'>
                                    &times;
                                </span>{' '}

                            </span>
                        </div>
                </div>
            </div>
                <div className="wrap-xl mx-auto">
                    {query && (
                        <SearchFlyoutNosto
                            suggestions={suggestions}
                            handleSuggestionClick={handleSuggestionClick}
                            results={results}
                            query={query}
                            collections={collections}
                            pages={pages}
                            loading={loading}
                            searched={searched}
                            redirect={redirect}
                        />
                    )}
                    {!query ? (
                        <SearchStatic
                            staticData={staticData}
                            handleSuggestionClick={handleSuggestionClick}
                        />
                    ) : null}
                </div>
            </div>
        </div>
    );
};

export default SearchNostoUI;
